import { DestroyRef, Injectable, InjectionToken, computed, effect, inject, signal, untracked } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlatformToolLayoutService } from '@core/services/platform-tool-layout.service';
import { viewportScale } from '@shared/helpers/viewport-scale';
import { documentZoom, installBrowserZoomGeometry, probeRectsFollowZoom, realViewportWidth, setDocumentZoom } from '@shared/helpers/browser-zoom-geometry';
import { MediaQueryScaler, scaleViewportUnits as scaleViewportUnitText } from '@shared/helpers/media-query-scale';
import { environment } from '../../../../environments/environment';
import { displayDensityEnabled } from '@shared/helpers/display-density-flag';
import {
  PREBOOT_VIEWPORT_SCALE_KEY,
  VIEWPORT_SCALED_APPLICATIONS,
  viewportScaleSwitchOn,
} from './viewport-scale.config';

/** The applications that scale: `VIEWPORT_SCALED_APPLICATIONS`, as a token so a spec can supply its own list. */
export const SCALED_APPLICATIONS = new InjectionToken<readonly string[]>('SCALED_APPLICATIONS', {
  providedIn: 'root',
  factory: () => VIEWPORT_SCALED_APPLICATIONS,
});

/**
 * GEN-E-105-01 — the runtime switch (`dynamicsettings.ViewportScale` in the deployment's environment.json, which
 * main.ts copies into `environment.dynamicsettings` before bootstrap). Off unless set: production stays unscaled
 * until the owner turns it on. A token so a spec can set it.
 */
export const VIEWPORT_SCALE_ENABLED = new InjectionToken<boolean>('VIEWPORT_SCALE_ENABLED', {
  providedIn: 'root',
  factory: () => viewportScaleSwitchOn((environment as any).dynamicsettings),
});

/**
 * GEN-E-105-05 — the one-off check that this browser reports rects the way the geometry patch assumes (native
 * rects in screen px under a root zoom). A token so a spec can supply the answer.
 */
export const GEOMETRY_CALIBRATION = new InjectionToken<(rootZoom: number) => boolean>('GEOMETRY_CALIBRATION', {
  providedIn: 'root',
  factory: () => {
    const doc = inject(DOCUMENT);
    return (rootZoom: number) => probeRectsFollowZoom(doc, rootZoom);
  },
});

/**
 * GEN-E-193-01 — reads the REAL window width, independent of the zoom this service sets (the patched innerWidth is
 * the floored effective viewport). A token so a spec can drive the width.
 */
export const VIEWPORT_WIDTH = new InjectionToken<() => number>('VIEWPORT_WIDTH', {
  providedIn: 'root',
  factory: () => {
    const view = inject(DOCUMENT).defaultView;
    return () => (view ? realViewportWidth(view) : 0);
  },
});

/**
 * GEN-E-99-33 — module-wise viewport scaling.
 *
 * While the current application (resolved from the URL by PlatformToolLayoutService, so it covers every route
 * a module owns, opened or not) is listed in VIEWPORT_SCALED_APPLICATIONS, the service publishes one contract
 * on <html> that the global stylesheet `styles/global/_viewport-scale.scss` reads:
 *
 *   --vs-zoom        the zoom: `viewportScale(innerWidth)` — width only (0.8 on a 1366 laptop, 1 at 1920) — plus
 *                    the display-density offset (GEN-E-99-68)
 *   html.vs-active   the page is zoomed: ONE `zoom` on <html>, like browser zoom
 *
 * GEN-E-99-68 — display density (Compact / Comfortable / Spacious, PlatformToolLayoutService) is an offset on this
 * base, not a zoom of its own: compact −0.1, spacious +0.1. 1366 laptop 0.70 / 0.80 / 0.90; 1920 0.90 / 1 / 1.10.
 * The density service decides when a tier applies and publishes its offset; this service applies the one zoom.
 *
 * GEN-E-99-58 — the whole page is zoomed at its root, so no authored value is recomputed anywhere (a 400px panel
 * is 400px, as under 80% browser zoom). The three things CSS zoom does not do that browser zoom does are made up
 * here, for the whole page: media queries answer for the effective viewport and viewport units resolve against
 * it (MediaQueryScaler), and the geometry APIs — rects, pointer coordinates, the viewport size — report CSS px
 * (browser-zoom-geometry). Anything else stays at 100%. Switching application flips it with no reload.
 */
/**
 * GEN-E-105-08 — how long a window resize must pause before the scale is recalculated. Each new zoom rewrites every
 * media rule and fires a resize to every listener; while the edge is dragged that would run many times a second.
 * The zoom itself is not rounded — the values stay exactly viewportScale(width) (owner decision, option A).
 */
export const RESIZE_SETTLE_MS = 150;

/** A floor for base + density offset, so a very small window at Compact still reads. */
const MIN_ZOOM = 0.5;

@Injectable({ providedIn: 'root' })
export class ViewportScaleService {
  private readonly document = inject(DOCUMENT);
  private readonly layout = inject(PlatformToolLayoutService);
  private readonly router = inject(Router);
  private readonly scaledApplications = inject(SCALED_APPLICATIONS);
  private readonly enabled = inject(VIEWPORT_SCALE_ENABLED);
  private readonly calibrate = inject(GEOMETRY_CALIBRATION);
  private readonly readWidth = inject(VIEWPORT_WIDTH);

  /**
   * GEN-E-105-05 — true once the browser check failed: this engine would get positions converted twice, so the
   * page is never zoomed here (100% with correct menus beats a smaller page with misplaced ones).
   */
  readonly unsupported = signal(false);
  private calibrated = false;

  private readonly viewportWidth = signal(this.readViewportWidth());

  /** Whether the current application scales. */
  readonly active = computed(() => {
    if (!this.enabled) return false; // GEN-E-105-01 — the switch is off: never a base scale
    const id = this.layout.currentApplicationModel()?.id;
    return !!id && (this.scaledApplications.includes('*') || this.scaledApplications.includes(id));
  });

  /** The base zoom: the viewport scale for a scaled module, 1 anywhere else. */
  readonly scale = computed(() => {
    return this.active() ? viewportScale(this.viewportWidth()) : 1;
  });

  /** GEN-E-99-68 — the display-density tier's offset on the base (0 when no tier is applied). */
  readonly densityOffset = computed(() => this.layout.densityZoomOffset?.() ?? 0);

  /**
   * GEN-E-105-03 — the zoom the index.html pre-boot painted with (1 when it did not scale). Read once, before the
   * first publish.
   */
  private readonly prebootZoom = readPrebootZoom(this.document.documentElement);

  /**
   * GEN-E-105-03 — true while the application cannot be known yet: no current application and the application
   * list not loaded. Publishing then would drop the pre-boot's zoom (active() is false without an application)
   * and bring it back once the list loads — a visible 0.80 -> 1 -> 0.80 jump on every load.
   */
  // GEN-E-105-13 — only while the scale is on: with the switch off the base is always 1, so there is nothing to
  // wait for, and waiting would hold a density choice back (forever for a user with an empty app switcher).
  readonly resolving = computed(() =>
    this.enabled && !this.layout.currentApplicationModel() && !this.layout.allApplications().length);

  /**
   * The zoom on <html>: the base plus the density offset, to two decimals — or, while resolving, the zoom the
   * pre-boot already painted, so boot shows one zoom from first paint to settled.
   */
  readonly zoom = computed(() => this.unsupported() ? 1 : this.resolving()
    ? this.prebootZoom
    : Math.round(Math.max(MIN_ZOOM, this.scale() + this.densityOffset()) * 100) / 100);

  constructor() {
    // GEN-E-105-01 — tell the next load's pre-boot whether to scale (it runs before environment.json is known).
    this.persistPrebootMarker(this.enabled && this.scaledApplications.length > 0);

    // GEN-E-105-07 — install the geometry patch up front whenever the zoom can move off 1 (the scale or density is
    // on), so a MediaQueryList created before the first zoom (e.g. by CDK at boot) is already zoom-aware. At zoom 1
    // every patched API passes straight through.
    if (this.enabled || displayDensityEnabled()) installBrowserZoomGeometry();

    // GEN-E-99-48 — the Classic (1.0) layout never loads the application list (only the v2.0 layout does), so
    // the current application stayed null there and nothing scaled. In Classic the service loads it and keeps
    // the current application in step with the URL itself; the v2.0 layout still owns it in v2.0.
    const destroyRef = inject(DestroyRef);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed(destroyRef))
      .subscribe(() => this.followApplicationInClassic());
    effect(() => {
      // Depends on the layout only; the application list it loads is written, not tracked.
      if (this.layout.isParticleAppLayout()) untracked(() => this.followApplicationInClassic());
    });

    const view = this.document.defaultView;
    if (view) {
      fromEvent(view, 'resize')
        .pipe(debounceTime(RESIZE_SETTLE_MS), takeUntilDestroyed(destroyRef))
        .subscribe(() => this.viewportWidth.set(this.readViewportWidth()));
    }

    effect(() => this.publish(this.zoom()));

    // GEN-E-99-62 — media queries answer for the effective viewport while the module is scaled (1 restores).
    const mediaQueries = new MediaQueryScaler(this.document);
    effect(() => mediaQueries.setZoom(this.zoom()));

    // GEN-E-105-04 — print at a true 100%: the print CSS resets the zoom and --vs-zoom/--vs-unit, and for the
    // print the geometry APIs and media queries answer for zoom 1 too; the screen value comes back afterwards.
    if (view) {
      fromEvent(view, 'beforeprint')
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
          setDocumentZoom(1);
          mediaQueries.setZoom(1);
        });
      fromEvent(view, 'afterprint')
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
          const zoom = untracked(() => this.zoom());
          setDocumentZoom(zoom);
          mediaQueries.setZoom(zoom);
        });
    }

    // GEN-E-99-63 — the page is zoomed, so `vh`/`vw` a dialog's opener sets inline (`maxHeight: '90vh'`) resolve
    // against the real viewport and come out off by the zoom; converted as each dialog opens.
    // ITP2-14376 / GEN-E-105-10 — every viewport length in the value, `calc()` and `min()` included (a side panel's
    // `calc(100vh - 60px)` ran past the bottom at Spacious and hid its Save / Cancel), and through `--vs-unit`, so a
    // dialog that is open while the zoom changes (a density switch, a resize across a class) still fits. Converted at
    // any zoom whenever the zoom can move off 1: at 1 the unit is 1.
    const zoomCanMove = this.enabled || displayDensityEnabled();
    inject(MatDialog, { optional: true })?.afterOpened
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(ref => {
        if (!zoomCanMove) return;
        // ITP2-14376 — found through the dialog's own component: MatDialog emits afterOpened inside open(), before
        // change detection renders the container's `[id]` host binding, so a lookup by `ref.id` found nothing and
        // every dialog went unconverted. The component is already attached in its pane when the event fires.
        const content = ref.componentRef?.location.nativeElement as HTMLElement | undefined;
        const pane = (content ?? this.document.getElementById(ref.id))?.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (pane) scalePaneViewportUnits(pane.style);
      });
  }

  private followApplicationInClassic(): void {
    if (!this.layout.isParticleAppLayout()) return;
    if (!this.layout.allApplications().length) this.layout.initializeApplications();
    this.layout.updateApplicationFromUrl();
  }

  private publish(zoom: number): void {
    const root = this.document.documentElement;
    if (!root) return;
    root.classList.toggle('vs-active', zoom !== 1);
    if (zoom !== 1) {
      root.style.setProperty('--vs-zoom', `${zoom}`);
    } else {
      root.style.removeProperty('--vs-zoom');
    }
    if (zoom === documentZoom()) return;
    // GEN-E-105-05 — check the browser once, with this zoom already on <html>, before the patch is installed.
    if (zoom !== 1 && !this.calibrated) {
      this.calibrated = true;
      if (!this.calibrate(zoom)) {
        console.warn('[ViewportScale] this browser does not report sizes the way the zoom correction expects; ' +
          'the page is shown at 100% (GEN-E-105-05).');
        this.persistPrebootMarker(false);
        this.unsupported.set(true); // zoom() becomes 1; the effect publishes that and removes the zoom
        return;
      }
    }
    setDocumentZoom(zoom);
    // The effective viewport just changed size: CDK's ViewportRuler, Kendo, gridster and angular-split cache it
    // and re-measure on `resize`, as they would when browser zoom changes. After the media rewrite (its effect).
    const view = this.document.defaultView;
    queueMicrotask(() => view?.dispatchEvent(new Event('resize')));
  }

  private persistPrebootMarker(on: boolean): void {
    try {
      const storage = this.document.defaultView?.localStorage;
      if (on) storage?.setItem(PREBOOT_VIEWPORT_SCALE_KEY, '1');
      else storage?.removeItem(PREBOOT_VIEWPORT_SCALE_KEY);
    } catch {
      // storage blocked — the pre-boot then never scales; the service still does once the app resolves
    }
  }

  /**
   * The REAL window width: innerWidth answers for the effective viewport while the page is zoomed. ITP2-14336 — read
   * it unzoomed, not as `innerWidth * zoom`: the patched innerWidth is floored, so that product falls short (under
   * the GEN-E-107-04 classes, gone since GEN-E-107-09, it put 1921 / 2560 / 3840 one step low or flickering). Read
   * through VIEWPORT_WIDTH (GEN-E-193-01, the native innerWidth) so a spec can drive it.
   */
  private readViewportWidth(): number {
    return this.readWidth();
  }
}

/** The zoom the pre-boot put on <html> (`html.vs-active` + `--vs-zoom`), or 1 when it put none. */
function readPrebootZoom(root: HTMLElement | null | undefined): number {
  if (!root?.classList.contains('vs-active')) return 1;
  const value = parseFloat(root.style.getPropertyValue('--vs-zoom'));
  return value > 0 && Number.isFinite(value) ? value : 1;
}

/**
 * `90vh` → `calc(90vh * var(--vs-unit, 1))`, `calc(100vh - 60px)` → `calc(calc(100vh * var(--vs-unit, 1)) - 60px)`, on
 * the inline sizes of a dialog pane (GEN-E-99-63, every form since ITP2-14376). Already converted values are left alone.
 */
function scalePaneViewportUnits(style: CSSStyleDeclaration): void {
  for (const property of ['height', 'min-height', 'max-height', 'width', 'min-width', 'max-width']) {
    const value = style.getPropertyValue(property);
    if (!value || value.includes('--vs-unit')) continue;
    const scaled = scaleViewportUnitText(value);
    if (scaled !== value) style.setProperty(property, scaled);
  }
}
